import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TblActionType } from 'src/app/shared/enums/tbl-action-type.enum';
import { ActGroupMessageViewModel, ActGroupMessageType } from './act-group-message.model';
import { RxFormBuilder } from '@rxweb/reactive-form-validators';
import { ProfileService } from '../../profile.service';
import { ProfileDetailModel } from '../../profile-detail.model';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-add-edit-actgroupmessage',
  templateUrl: './add-edit-act-group-message.component.html',
  styleUrls: ['./add-edit-act-group-message.component.scss']
})
export class AddEditActGroupMessageComponent implements OnInit {

  dialogTitle: string;
  actGroupMessageModel: ActGroupMessageViewModel;
  actGroupMessageFrm: FormGroup;
  isEditMode: boolean;
  action: TblActionType;
  actionType = TblActionType;
  isSavingActGroupMessage: boolean = false;
  profileList: ProfileDetailModel[] = [];
  // Expose enum to template
  messageTypes = [
    { value: ActGroupMessageType.Info, label: 'Info' },
    { value: ActGroupMessageType.Warning, label: 'Warning' },
    { value: ActGroupMessageType.Error, label: 'Error' }
  ];
  profileNotBlockList: string[];
  isLoadingProfile: boolean = false;
  isFetchingActGroupMessage = false;

  // Track initial state for change detection
  private initialFormValue: any;
  private initialProfileNotBlockList: string[];

  constructor(
    public dialogRef: MatDialogRef<AddEditActGroupMessageComponent>,
    private profileService: ProfileService,
    private frmBuilder: RxFormBuilder,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.action = data.action;
    this.dialogTitle = this.action === this.actionType.Edit ? "Edit Act Group Message" : "Add Act Group Message";
  }

  ngOnInit() {
    this.setupData();
  }

  async setupData() {
    this.actGroupMessageFrm = this.frmBuilder.formGroup(
      ActGroupMessageViewModel,
      new ActGroupMessageViewModel()
    );

    await this.fetchActGroupMessageByActGroupId();

    this.actGroupMessageFrm.patchValue({
      ...this.actGroupMessageModel,
      profileId: this.data.profileId,
      actGroupId: this.data.actGroupId,
      type: this.actGroupMessageModel.type ?? ActGroupMessageType.Warning
    });

    await this.setupProfileList();

    // Store initial state after all data is loaded
    this.storeInitialState();
  }

  async setupProfileList() {
    try {
      if (this.data?.profileId) {
        this.isLoadingProfile = true;
        const profileListResult = await this.profileService.getProfileRelationshipsByActGroupId(this.data.actGroupId).toPromise();
        if (profileListResult.result) {
          this.profileList = profileListResult.result;
          this.profileNotBlockList = this.profileList.filter(x => x.profileAdditionDetail.isGroupMessage).map(x => x.profileId);
        }
      }
    }
    catch (ex) {
      console.error(ex);
    }
    finally {
      this.isLoadingProfile = false;
    }
  }

  async fetchActGroupMessageByActGroupId() {
    try {
      this.isFetchingActGroupMessage = true;

      const res = await this.profileService
        .getActGroupMessageByActGroupId(this.data.actGroupId)
        .toPromise();

      this.actGroupMessageModel = res?.result
        ? res.result
        : new ActGroupMessageViewModel();

    } catch (ex) {
      console.error(ex);
    } finally {
      this.isFetchingActGroupMessage = false;
    }
  }

  /**
   * Store the initial state for change detection
   */
  private storeInitialState(): void {
    this.initialFormValue = { ...this.actGroupMessageFrm.value };
    this.initialProfileNotBlockList = [...(this.profileNotBlockList || [])];
  }

  /**
   * Check if there are any unsaved changes
   */
  hasUnsavedChanges(): boolean {
    // Check if form values have changed
    const formChanged = JSON.stringify(this.initialFormValue) !== JSON.stringify(this.actGroupMessageFrm.value);

    // Check if profile selection has changed
    const profileListChanged = this.hasProfileListChanged();

    return formChanged || profileListChanged;
  }

  /**
   * Check if profile selection list has changed
   */
  private hasProfileListChanged(): boolean {
    if (!this.initialProfileNotBlockList || !this.profileNotBlockList) {
      return false;
    }

    // Compare lengths
    if (this.initialProfileNotBlockList.length !== this.profileNotBlockList.length) {
      return true;
    }

    // Compare contents (sorted to avoid order issues)
    const initialSorted = [...this.initialProfileNotBlockList].sort();
    const currentSorted = [...this.profileNotBlockList].sort();

    return JSON.stringify(initialSorted) !== JSON.stringify(currentSorted);
  }

  async onCancel() {
    // Only show confirmation if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      const result = await this.dialog
        .open(ConfirmModalComponent, {
          data: {
            message: 'You have unsaved changes. Do you wish to discard them?'
          }
        })
        .afterClosed()
        .toPromise();

      if (result) {
        this.dialogRef.close();
      }
    } else {
      // No changes, close directly
      this.dialogRef.close();
    }
  }

  async onSave() {
    try {
      this.isSavingActGroupMessage = true;
      if (this.actGroupMessageFrm.valid) {
        const result = await this.profileService.saveActGroupMessage(this.actGroupMessageFrm.value).toPromise();
        if (result.result) {
          await this.profileService.toggleIsGroupMessageForProfiles(result.result.actGroupId, this.profileNotBlockList).toPromise();
          this.dialogRef.close(true);
        }
      }
    }
    catch (ex) {
      console.error(ex);
    }
    finally {
      this.isSavingActGroupMessage = false;
    }
  }

  // Helper method to get form control errors
  getErrorMessage(fieldName: string): string {
    const control = this.actGroupMessageFrm.get(fieldName);
    if (control?.hasError('required')) {
      return 'This field is required';
    }
    if (control?.hasError('minLength')) {
      return `Minimum length is ${control.errors?.['minLength'].requiredLength}`;
    }
    if (control?.hasError('maxLength')) {
      return `Maximum length is ${control.errors?.['maxLength'].requiredLength}`;
    }
    return '';
  }

  onAddProfileIdToGroupMessage(profileId: string) {
    const existingProfileId = this.profileNotBlockList.find(x => x === profileId);
    if (!existingProfileId) {
      this.profileNotBlockList.push(profileId);
    } else {
      this.profileNotBlockList = this.profileNotBlockList.filter(x => x !== profileId);
    }
  }

  isAllProfilesSelected(): boolean {
    if (!this.profileList || this.profileList.length === 0) {
      return false;
    }
    return this.profileNotBlockList?.length === this.profileList.length;
  }

  isSomeProfilesSelected(): boolean {
    if (!this.profileList || this.profileList.length === 0) {
      return false;
    }
    const selectedCount = this.profileNotBlockList?.length || 0;
    return selectedCount > 0 && selectedCount < this.profileList.length;
  }

  onSelectAllProfiles(event: any): void {
    const isChecked = event.checked;

    if (this.profileList) {
      // Update each profile's state
      this.profileList.forEach(profile => {
        profile.profileAdditionDetail.isGroupMessage = isChecked;
      });

      // Sync the profileNotBlockList array
      if (isChecked) {
        // Select all: add all profile IDs to the list
        this.profileNotBlockList = this.profileList.map(p => p.profileId);
      } else {
        // Deselect all: clear the list
        this.profileNotBlockList = [];
      }
    }
  }
}